import { isoDateTime, z } from "../zod.js";
import { Currency, Uuid } from "../identity/admin-schemas.js";

// Module 14 agents, slice 14-A: the contract of spec 14 §5's rows 14-A owns
// (D-512 ruling 2) and the problem-code catalog of §5. P3-K K-2 WP-E0.
//
// EVERY JSONB THE API SERVES IS TYPED (D-506 ruling 8). An action's
// `evidence`, a guardrail event's `detail`, a policy row's `policy_params` and
// a run's `trigger_ref`, `outcome` and `error` are closed shapes here, never an
// opaque object, so the D-301 value census (sales-value-matrix.int.test.ts)
// sees every money key the agents API can serve, and D-339's response contract
// refuses a key nobody named. An action card is a union keyed on `tool_code`:
// one variant per tool whose verdict evidence carries a money key, naming
// those keys, and one closed generic variant whose evidence holds counts,
// refs and the policy's own detail and no money. `apps/api/test/unit/
// agents-evidence-contract.test.ts` holds each registered write's
// `evidenceSchema` to its variant (or to the generic variant's keys), so a tool
// that starts serving money under the generic variant reds `ci:unit`.
//
// Money is integer minor units beside a `currency` (or `*_currency`); a value
// fenced from the reader is OMITTED, never nulled (spec 14 §7.5), so every
// money key below is optional.

const Timestamp = isoDateTime();
const PageCursor = z.string().optional();
const PageLimit = z.coerce.number().int().min(1).max(100).optional();
const NextCursor = z.string().nullable();
const MinorUnits = z.number().int();
const Count = z.number().int().min(0);

/** `agents.agent_code` / `agent_prompt_versions.agent_code` (D-506 ruling 6). */
export const AgentCode = z
  .string()
  .regex(/^[a-z][a-z0-9_]{2,30}$/)
  .openapi({ example: "offer_agent" });

/** The five builtin agents (spec 14 §3.8). 14-A ensures `offer_agent` alone (D-507 ruling 2). */
export const BUILTIN_AGENT_CODES = [
  "ops_copilot",
  "offer_agent",
  "planner_agent",
  "qc_watcher",
  "finance_clerk",
] as const;

export const AgentKind = z.enum(["builtin", "custom"]);
export const AgentStatus = z.enum(["enabled", "paused", "disabled"]);
/** A tool's declared autonomy (spec 14 §3.3). */
export const AgentAutonomy = z.enum(["autonomous", "autonomous_with_cap", "threshold", "ask"]);
/** What a §2.4 policy row may set: an autonomy, or `disabled` (D-509 ruling 1). */
export const AgentPolicyAutonomy = z.enum([
  "autonomous",
  "autonomous_with_cap",
  "threshold",
  "ask",
  "disabled",
]);
export const AgentToolKind = z.enum(["read", "write"]);
export const AgentToolImpact = z.enum(["money", "schedule", "message", "none"]);
export const AgentReversibility = z.enum(["reversible", "compensatable", "irreversible"]);
export const AgentToolAvailability = z.enum(["available", "unavailable"]);
export const AgentRunStatus = z.enum([
  "queued",
  "running",
  "awaiting_approval",
  "completed",
  "failed",
  "cancelled",
]);
export const AgentTriggerType = z.enum(["message", "event", "schedule", "manual"]);
export const AgentActionStatus = z.enum([
  "proposed",
  "approved",
  "rejected",
  "executing",
  "executed",
  "failed",
  "undone",
  "expired",
]);
export const AgentAutonomyApplied = z.enum([
  "ask",
  "forced_ask",
  "threshold_pass",
  "threshold_exceeded_ask",
  "cap_exceeded_ask",
  "autonomous",
  "autonomous_with_cap",
  "human_initiated",
]);
/** How an undo would act for this caller (D-511 ruling 2). */
export const AgentUndoMode = z.enum(["reverse", "compensate_tool", "compensate_manual", "none"]);
export const AgentPromptSource = z.enum(["launch_pack", "platform_update", "tenant_override"]);
export const AgentPolicySource = z.enum(["explicit", "declared"]);
export const AgentGuardrailKind = z.enum([
  "prompt_injection_suspected",
  "tool_denied_visibility",
  "tool_denied_policy",
  "cap_action_exceeded",
  "cap_spend_exceeded",
  "cap_token_exceeded",
  "schema_validation_failed",
  "citation_missing",
  "scope_violation_blocked",
  "tool_orphaned",
  "run_limit_exceeded",
]);
export const AgentCapKind = z.enum(["action", "spend", "token"]);
export const AgentScopeType = z.enum(["group", "facility", "department"]);
/** A tenant-zone month, `YYYY-MM` (D-509 ruling 7). */
export const AgentMonth = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
  .openapi({ example: "2026-10" });
/** A tenant-zone business date. */
export const AgentDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .openapi({ format: "date", example: "2026-10-19" });

/** One subject of a run (spec 14 §2.8), e.g. `{kind: "customer", id}`. */
export const AgentSubjectRef = z
  .object({ kind: z.string().regex(/^[a-z][a-z_]{1,40}$/), id: Uuid })
  .strict()
  .openapi("AgentSubjectRef");

/** An i18n outcome key with its params (D-225; D-515 ruling 6's `agents.outcome.*`). */
export const AgentRunOutcome = z
  .object({
    key: z.string().regex(/^agents\.outcome\.[a-z_]+$/),
    params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
  })
  .strict()
  .openapi("AgentRunOutcome");

/** A problem code a run or an action ended with, and the attempt it ended on. */
export const AgentErrorRef = z
  .object({
    code: z.string().regex(/^[A-Z][A-Z0-9_]+$/),
    attempt: z.number().int().min(1).optional(),
  })
  .strict()
  .openapi("AgentErrorRef");

// --- agents (spec 14 §2.2, §4.2, §5) -----------------------------------------

/** An agent's caps. `monthly_spend_cap_minor` is money in `spend_cap_currency`. */
export const AgentCaps = z
  .object({
    monthly_action_cap: Count,
    monthly_spend_cap_minor: MinorUnits.min(0),
    spend_cap_currency: Currency,
    daily_token_budget: Count,
  })
  .strict()
  .openapi("AgentCaps");

/**
 * Month-to-date usage in the tenant's zone (D-512 rulings 4 and 5).
 * `spend_minor` is served only to a reader holding every contributing tool's
 * value grants; counts are served to every reader.
 */
export const AgentUsageMtd = z
  .object({
    month: AgentMonth,
    actions_unsupervised: Count,
    spend_minor: MinorUnits.min(0).optional(),
    spend_currency: Currency.optional(),
    /** A `cap_spend_exceeded` event this month newer than `spend_cap_changed_at`. */
    cap_reached: z.boolean(),
  })
  .strict()
  .openapi("AgentUsageMtd");

export const AgentActivityDay = z
  .object({ date: AgentDate, actions: Count })
  .strict()
  .openapi("AgentActivityDay");

export const AgentLastRun = z
  .object({ id: Uuid, status: AgentRunStatus, finished_at: Timestamp.nullable() })
  .strict()
  .openapi("AgentLastRun");

/** One agent on the Hub (spec 14 §5 `GET /v1/agents`). `display_name` null renders
 * `agents.catalog.<agent_code>.name`. `tools_registered` false means the enable
 * control is not rendered (D-512 ruling 3); the missing names are served only
 * on the 422 `AGENT_TOOLS_MISSING`. */
export const AgentListItem = z
  .object({
    id: Uuid,
    agent_code: AgentCode,
    kind: AgentKind,
    display_name: z.string().min(1).max(80).nullable(),
    status: AgentStatus,
    status_changed_by: Uuid.nullable(),
    status_changed_at: Timestamp.nullable(),
    model_profile: z.string().min(1).max(64),
    version: z.number().int().min(1),
    tools_registered: z.boolean(),
    caps: AgentCaps,
    usage_mtd: AgentUsageMtd,
    tokens_today: Count,
    activity_30d: z.array(AgentActivityDay).max(31),
    last_run: AgentLastRun.nullable(),
  })
  .strict()
  .openapi("AgentListItem");

export const AgentListResponse = z
  .object({ items: z.array(AgentListItem) })
  .strict()
  .openapi("AgentListResponse");

/** One of the agent's module 16 memberships (spec 14 §2.3). */
export const AgentMembership = z
  .object({
    membership_id: Uuid,
    scope_type: AgentScopeType,
    facility_id: Uuid.nullable(),
    department_id: Uuid.nullable(),
    role_code: z.string(),
  })
  .strict()
  .openapi("AgentMembership");

export const AgentActivePrompt = z
  .object({
    id: Uuid,
    version: z.number().int().min(1),
    source: AgentPromptSource,
    platform_version: z.number().int().min(1).nullable(),
  })
  .strict()
  .openapi("AgentActivePrompt");

export const AgentDetail = AgentListItem.extend({
  memberships: z.array(AgentMembership),
  active_prompt: AgentActivePrompt,
  spend_cap_changed_at: Timestamp.nullable(),
  created_at: Timestamp,
  updated_at: Timestamp,
}).openapi("AgentDetail");

/** `PATCH /v1/agents/:id`. Status and display name need `agents.manage`;
 * caps and the model profile need `agents.config.manage` (spec 14 §5). */
export const AgentPatchRequest = z
  .object({
    status: AgentStatus.optional(),
    display_name: z.string().trim().min(1).max(80).nullable().optional(),
    model_profile: z.string().min(1).max(64).optional(),
    monthly_action_cap: z.number().int().min(0).max(1_000_000).optional(),
    monthly_spend_cap_minor: MinorUnits.min(0).optional(),
    daily_token_budget: z.number().int().min(0).max(1_000_000_000).optional(),
    version: z.number().int().min(1),
  })
  .strict()
  .openapi("AgentPatchRequest");

export const AgentScopeBinding = z
  .object({
    scope_type: AgentScopeType,
    facility_id: Uuid.optional(),
    department_id: Uuid.optional(),
  })
  .strict()
  .openapi("AgentScopeBinding");

export const AgentScopeBindingsRequest = z
  .object({ bindings: z.array(AgentScopeBinding).max(200) })
  .strict()
  .openapi("AgentScopeBindingsRequest");

export const AgentScopeBindingsResponse = z
  .object({ memberships: z.array(AgentMembership) })
  .strict()
  .openapi("AgentScopeBindingsResponse");

// --- settings (spec 14 §2.12) ------------------------------------------------

export const AgentSettings = z
  .object({
    facility_policy_money_ceiling_minor: MinorUnits.min(0),
    facility_policy_money_ceiling_currency: Currency,
    updated_by: Uuid,
    updated_at: Timestamp,
  })
  .strict()
  .openapi("AgentSettings");

export const AgentSettingsPatchRequest = z
  .object({ facility_policy_money_ceiling_minor: MinorUnits.min(0) })
  .strict()
  .openapi("AgentSettingsPatchRequest");

// --- tools (spec 14 §2.1, §5 `GET /v1/agents/tools`) -------------------------

/** A JSON Schema document the registry generated (2020-12). Not data: a
 * tool's params and policy shapes as the model and the policy editor read
 * them, which carry no tenant value. */
const JsonSchemaDocument = z.record(z.string(), z.unknown()).openapi({
  description: "A JSON Schema 2020-12 document generated from the tool's zod schema",
});

export const AgentToolListQuery = z
  .object({
    module: z
      .string()
      .regex(/^[a-z]+$/)
      .optional(),
    kind: AgentToolKind.optional(),
  })
  .strict();

export const AgentToolItem = z
  .object({
    tool_code: z.string().regex(/^[a-z]+\.[a-z0-9_]+$/),
    module: z.string(),
    version: z.number().int().min(1),
    kind: AgentToolKind,
    params_schema: JsonSchemaDocument,
    impact: AgentToolImpact.nullable(),
    reversibility: AgentReversibility.nullable(),
    default_autonomy: AgentAutonomy,
    max_autonomy: AgentAutonomy,
    threshold_rule: z
      .object({ kind: z.string() })
      .catchall(z.union([z.string(), z.number(), z.boolean(), z.null()]))
      .nullable(),
    policy_params_schema: JsonSchemaDocument.nullable(),
    value_grants: z.array(z.string()),
    value_roles: z.array(z.string()).nullable(),
    availability: AgentToolAvailability,
    waiting_on: z.string().nullable(),
    deprecated: z.boolean(),
    globally_disabled: z.boolean(),
  })
  .strict()
  .openapi("AgentToolItem");

export const AgentToolListResponse = z
  .object({ items: z.array(AgentToolItem) })
  .strict()
  .openapi("AgentToolListResponse");

// --- policies (spec 14 §2.4) -------------------------------------------------

/** A single money cap: `inventory.reserve_stock` (D-526 ruling 1) and
 * `sales.send_quote` with its reliability floor (D-517 ruling 1). */
export const AgentCapPolicyParams = z
  .object({
    cap_minor: MinorUnits.min(0),
    cap_currency: Currency,
    min_reliability: z.number().int().min(0).max(100).optional(),
  })
  .strict()
  .openapi("AgentCapPolicyParams");

/** `planning.firm_planned_orders`' per-facility caps (D-526 ruling 2); a
 * facility absent from the array asks with `cap_not_configured`. */
export const AgentFacilityCapsPolicyParams = z
  .object({
    facility_caps: z
      .array(
        z
          .object({ facility_id: Uuid, cap_minor: MinorUnits.min(0), cap_currency: Currency })
          .strict(),
      )
      .max(200),
  })
  .strict()
  .openapi("AgentFacilityCapsPolicyParams");

/** Every `policyParams` shape a registered tool declares. The policy door
 * validates against the TOOL's own zod; this union is what the API serves. A
 * tool that declares a new shape adds it here in the same commit. */
export const AgentPolicyParams = z
  .union([AgentCapPolicyParams, AgentFacilityCapsPolicyParams])
  .openapi("AgentPolicyParams");

export const AgentPolicyUsage30d = z
  .object({ proposed: Count, executed: Count })
  .strict()
  .openapi("AgentPolicyUsage30d");

export const AgentPolicyItem = z
  .object({
    tool_code: z.string(),
    kind: AgentToolKind,
    autonomy: AgentPolicyAutonomy,
    max_autonomy: AgentAutonomy,
    policy_params: AgentPolicyParams.nullable(),
    /** The row's version; null when no row exists (`source = declared`). */
    version: z.number().int().min(1).nullable(),
    source: AgentPolicySource,
    orphaned: z.boolean(),
    usage_30d: AgentPolicyUsage30d,
  })
  .strict()
  .openapi("AgentPolicyItem");

export const AgentPolicyListResponse = z
  .object({ items: z.array(AgentPolicyItem) })
  .strict()
  .openapi("AgentPolicyListResponse");

export const AgentPolicyPutRequest = z
  .object({
    autonomy: AgentPolicyAutonomy,
    policy_params: AgentPolicyParams.nullable().optional(),
    /** The row's version when one exists; absent for a first write. */
    version: z.number().int().min(1).optional(),
  })
  .strict()
  .openapi("AgentPolicyPutRequest");

export const AgentPolicyRow = z
  .object({
    agent_id: Uuid,
    tool_code: z.string(),
    autonomy: AgentPolicyAutonomy,
    policy_params: AgentPolicyParams.nullable(),
    version: z.number().int().min(1),
    updated_by: Uuid,
    updated_at: Timestamp,
  })
  .strict()
  .openapi("AgentPolicyRow");

// --- prompt versions (spec 14 §2.5, D-532) -----------------------------------

export const AgentPromptVersion = z
  .object({
    id: Uuid,
    agent_code: AgentCode,
    version: z.number().int().min(1),
    source: AgentPromptSource,
    platform_version: z.number().int().min(1).nullable(),
    system_prompt_md: z.string(),
    changelog: z.string(),
    is_active: z.boolean(),
    created_by: Uuid,
    created_at: Timestamp,
  })
  .strict()
  .openapi("AgentPromptVersion");

export const AgentPromptVersionListResponse = z
  .object({ items: z.array(AgentPromptVersion) })
  .strict()
  .openapi("AgentPromptVersionListResponse");

export const AgentPromptVersionCreateRequest = z
  .object({
    system_prompt_md: z.string().trim().min(1).max(50_000),
    changelog: z.string().trim().min(1).max(2_000),
  })
  .strict()
  .openapi("AgentPromptVersionCreateRequest");

export const AgentPromptActivateResponse = z
  .object({ activated: z.literal(true) })
  .strict()
  .openapi("AgentPromptActivateResponse");

// --- triggers and runs (spec 14 §2.8, §5) -------------------------------------

/** A manual trigger's ref. 14-A's one shape is the offer agent's
 * `{kind: "message", message_id}` (D-515 ruling 4); a later agent adds its
 * own variant. */
export const AgentTriggerRef = z
  .discriminatedUnion("kind", [z.object({ kind: z.literal("message"), message_id: Uuid }).strict()])
  .openapi("AgentTriggerRef");

export const AgentTriggerRequest = z
  .object({ trigger_ref: AgentTriggerRef })
  .strict()
  .openapi("AgentTriggerRequest");

export const AgentTriggerResponse = z
  .object({ run_id: Uuid })
  .strict()
  .openapi("AgentTriggerResponse");

/** A run's `trigger_ref` as stored: the event it consumed or the manual ref,
 * the message context, and the caller the server stamps on a manual run
 * (D-509 ruling 12). Ids and codes only. */
export const AgentRunTriggerRef = z
  .object({
    kind: z.enum(["message", "event"]).optional(),
    event: z
      .string()
      .regex(/^[a-z]+\.[a-z0-9_]+$/)
      .optional(),
    event_id: Uuid.optional(),
    message_id: Uuid.optional(),
    conversation_id: Uuid.optional(),
    contact_id: Uuid.optional(),
    requested_by: Uuid.optional(),
  })
  .strict()
  .openapi("AgentRunTriggerRef");

export const AgentRun = z
  .object({
    id: Uuid,
    agent_id: Uuid,
    agent_code: AgentCode,
    trigger_type: AgentTriggerType,
    trigger_ref: AgentRunTriggerRef,
    subject_refs: z.array(AgentSubjectRef),
    model_profile: z.string(),
    prompt_version_id: Uuid,
    attempt: z.number().int().min(1),
    next_attempt_at: Timestamp.nullable(),
    status: AgentRunStatus,
    outcome: AgentRunOutcome.nullable(),
    error: AgentErrorRef.nullable(),
    last_error: AgentErrorRef.nullable(),
    input_tokens: Count,
    output_tokens: Count,
    tool_calls_count: Count,
    started_at: Timestamp.nullable(),
    finished_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .strict()
  .openapi("AgentRun");

export const AgentRunListQuery = z
  .object({
    agent_id: Uuid.optional(),
    status: AgentRunStatus.optional(),
    trigger_type: AgentTriggerType.optional(),
    message_id: Uuid.optional(),
    from: Timestamp.optional(),
    to: Timestamp.optional(),
    cursor: PageCursor,
    limit: PageLimit,
  })
  .strict();

export const AgentRunListResponse = z
  .object({ items: z.array(AgentRun), next_cursor: NextCursor })
  .strict()
  .openapi("AgentRunListResponse");

// --- guardrail events (spec 14 §2.11), `detail` typed per kind ----------------

const guardrailBase = {
  id: Uuid,
  agent_id: Uuid,
  run_id: Uuid.nullable(),
  created_at: Timestamp,
};

const guardrail = <K extends z.infer<typeof AgentGuardrailKind>, D extends z.ZodRawShape>(
  kind: K,
  detail: D,
) =>
  z.object({ ...guardrailBase, kind: z.literal(kind), detail: z.object(detail).strict() }).strict();

/** One guardrail event. `cap_spend_exceeded` carries money, served under the
 * contributing tools' value grants and omitted otherwise. */
export const AgentGuardrailEvent = z
  .discriminatedUnion("kind", [
    guardrail("prompt_injection_suspected", {
      matched_pattern: z.string().max(200),
      locale: z.string().max(10).optional(),
      source: z.string().max(64),
      message_ref: z.string().max(80).optional(),
      tool_code: z.string().optional(),
    }),
    guardrail("tool_denied_visibility", {
      tool_code: z.string(),
      missing_permission: z.string().optional(),
    }),
    guardrail("tool_denied_policy", {
      tool_code: z.string(),
      reason: z.string().max(64),
    }),
    guardrail("cap_action_exceeded", {
      month: AgentMonth,
      cap_value: Count,
      current_value: Count,
    }),
    guardrail("cap_spend_exceeded", {
      month: AgentMonth,
      cap_minor: MinorUnits.min(0).optional(),
      spend_minor: MinorUnits.min(0).optional(),
      impact_minor: MinorUnits.optional(),
      currency: Currency.optional(),
      tool_code: z.string(),
    }),
    guardrail("cap_token_exceeded", {
      scope: z.enum(["agent_day", "platform_day"]),
      date: AgentDate,
      budget: Count,
      used: Count,
    }),
    guardrail("schema_validation_failed", {
      tool_code: z.string(),
      attempt: z.number().int().min(1),
    }),
    guardrail("citation_missing", {
      message_ref: z.string().max(80).optional(),
    }),
    guardrail("scope_violation_blocked", {
      tool_code: z.string(),
      facility_id: Uuid.nullable().optional(),
      department_id: Uuid.nullable().optional(),
    }),
    guardrail("tool_orphaned", {
      tool_code: z.string(),
      action_id: Uuid.optional(),
    }),
    // §9 item 16: a run reached a limit and ended failed (D-543 ruling 6).
    guardrail("run_limit_exceeded", {
      limit: z.enum(["turns", "wall_clock"]),
      value: z.number().int().min(0),
    }),
  ])
  .openapi("AgentGuardrailEvent");

export const AgentGuardrailListQuery = z
  .object({
    kind: AgentGuardrailKind.optional(),
    agent_id: Uuid.optional(),
    from: Timestamp.optional(),
    to: Timestamp.optional(),
    cursor: PageCursor,
    limit: PageLimit,
  })
  .strict();

export const AgentGuardrailListResponse = z
  .object({ items: z.array(AgentGuardrailEvent), next_cursor: NextCursor })
  .strict()
  .openapi("AgentGuardrailListResponse");

/** `GET /v1/agent-runs/:id`: the run, its guardrail events and its action ids
 * (no timeline in 14-A, D-512 ruling 2). */
export const AgentRunDetail = AgentRun.extend({
  guardrail_events: z.array(AgentGuardrailEvent),
  action_ids: z.array(Uuid),
}).openapi("AgentRunDetail");

// --- action evidence, typed per tool (D-506 ruling 8) -------------------------

/** Keys every stored evidence may carry beside the verdict's own: the
 * verdict's machine reason, and the forced-ask reason (D-509 ruling 1). */
const evidenceCommon = {
  verdict_reason: z.string().max(64).optional(),
  forced_reason: z.string().max(64).optional(),
};
const OptMinor = MinorUnits.optional();
const OptNullableMinor = MinorUnits.nullable().optional();
const OptCurrency = z.string().length(3).optional();
const OptNullableCurrency = z.string().length(3).nullable().optional();
/** Spec 09 §3.2's price sources and §3.3's fulfilment options, as the sales
 * evidence names them. */
const SalesPriceSource = z.enum(["tier_list", "customer_override", "offcut_class", "manual"]);
const SalesFulfillmentKind = z.enum(["offcut_combo", "stock_unit", "production"]);

/**
 * The evidence of each write whose verdict carries a money key, by tool.
 * Each mirrors the tool's own `evidenceSchema` key for key, with every money
 * key optional because the value fence omits it (spec 14 §7.5), at every
 * depth: the sales variants' lines and alternatives carry money one level
 * down, and the fence removes it there too (D-550).
 */
export const AGENT_MONEY_EVIDENCE = {
  "inventory.reserve_stock": z
    .object({
      qty_base: z.string().nullable(),
      base_uom: z.string(),
      basis: z.enum(["requested_quantity", "unit_balance", "dearest_lot_at_facility"]),
      unvalued: z.boolean(),
      unit_cost_minor: OptNullableMinor,
      est_value_minor: OptNullableMinor,
      currency: z.string().nullable().optional(),
      cap_minor: OptNullableMinor,
      cap_currency: z.string().nullable().optional(),
      redacted: z.literal(true).optional(),
      impact_minor: OptMinor,
      impact_currency: z.string().optional(),
      ...evidenceCommon,
    })
    .strict(),
  "inventory.flag_quarantine": z
    .object({
      unvalued: z.boolean(),
      redacted: z.literal(true).optional(),
      impact_minor: OptMinor,
      impact_currency: z.string().optional(),
      ...evidenceCommon,
    })
    .strict(),
  "inventory.adjust_stock": z
    .object({
      unvalued: z.boolean(),
      redacted: z.literal(true).optional(),
      impact_minor: OptMinor,
      impact_currency: z.string().optional(),
      ...evidenceCommon,
    })
    .strict(),
  "planning.firm_planned_orders": z
    .object({
      facility_id: Uuid.nullable().optional(),
      facilities: Count.optional(),
      redacted: z.boolean(),
      impact_minor: MinorUnits.min(0).optional(),
      impact_currency: OptCurrency,
      ...evidenceCommon,
    })
    .strict(),
  "quality.place_hold": z
    .object({
      value_at_risk_minor: OptNullableMinor,
      currency: OptNullableCurrency,
      cap_minor: z.null().optional(),
      impact_minor: OptMinor,
      impact_currency: OptCurrency,
      attach_to: z.string().optional(),
      redacted: z.boolean().optional(),
      ...evidenceCommon,
    })
    .strict(),
  "quality.execute_disposition": z
    .object({
      hold_no: z.string(),
      impact_minor: OptMinor,
      impact_currency: OptCurrency,
      redacted: z.boolean().optional(),
      ...evidenceCommon,
    })
    .strict(),
  "quality.request_rework": z
    .object({
      labor_cost_minor: z.null().optional(),
      cap_minor: z.null().optional(),
      status: z.string().optional(),
      resolution_status: z.string().optional(),
      redacted: z.boolean().optional(),
      ...evidenceCommon,
    })
    .strict(),
  "waste.set_disposition": z
    .object({
      cost_minor: OptMinor,
      impact_minor: OptMinor,
      impact_currency: OptNullableCurrency,
      cap_minor: OptMinor,
      cap_currency: OptNullableCurrency,
      redacted: z.boolean(),
      ...evidenceCommon,
    })
    .strict(),
  // K-3 (D-516 ruling 2, D-538 ruling 4): the draft's proposed lines priced
  // by the engine on the option each would be drafted on.
  "sales.draft_quote": z
    .object({
      currency: z.string().length(3),
      total_minor: OptMinor,
      lines: z.array(
        z
          .object({
            product_id: z.string(),
            description: z.string(),
            qty: z.string(),
            uom: z.string(),
            price_source: SalesPriceSource,
            unit_price_minor: OptMinor,
            line_total_minor: OptMinor,
            fulfillment_kind: SalesFulfillmentKind.optional(),
          })
          .strict(),
      ),
      redacted: z.literal(true).optional(),
      ...evidenceCommon,
    })
    .strict(),
  // K-3 (D-517 ruling 2): the send's verdict inputs, its priced lines and the
  // ranked fulfilment alternatives of each line.
  "sales.send_quote": z
    .object({
      quote_id: Uuid,
      quote_no: z.string(),
      revision_no: z.number().int(),
      currency: z.string().length(3),
      total_minor: OptMinor,
      cap_minor: OptMinor,
      cap_currency: OptCurrency,
      reliability_score: z.number().nullable(),
      min_reliability: z.number().nullable(),
      customer_sends_7d: Count,
      off_list_lines: Count,
      margin_breached: z.boolean(),
      lines: z.array(
        z
          .object({
            line_no: z.number().int(),
            description: z.string(),
            qty: z.string(),
            uom: z.string(),
            price_source: SalesPriceSource,
            unit_price_minor: OptMinor,
            line_total_minor: OptMinor,
          })
          .strict(),
      ),
      alternatives: z.array(
        z
          .object({
            line_no: z.number().int(),
            rank: z.number().int().min(1),
            kind: SalesFulfillmentKind,
            chosen: z.boolean(),
            pieces: Count.optional(),
            unit_price_minor: OptMinor,
            line_total_minor: OptMinor,
          })
          .strict(),
      ),
      redacted: z.literal(true).optional(),
      impact_minor: OptMinor,
      impact_currency: OptCurrency,
      ...evidenceCommon,
    })
    .strict(),
} as const;

export type AgentMoneyEvidenceTool = keyof typeof AGENT_MONEY_EVIDENCE;

/**
 * The evidence of every other write: counts, refs, the policy's own detail
 * (a batch rule's items, a rate limit's ledger, a write scope) and the
 * verdicts that carry no money. Closed, and no key is money, so a tool that
 * starts returning money here fails the response contract.
 */
export const AgentGenericEvidence = z
  .object({
    impact_count: Count.optional(),
    // `sales.reply_order_status`: the order the status card is about (K-3).
    order_no: z.string().optional(),
    message_kind: z.string().nullable().optional(),
    status: z.string().optional(),
    shortage_count: Count.optional(),
    qty_planned: z.string().optional(),
    qty_cap: z.null().optional(),
    current_scrap_pct: z.number().nullable().optional(),
    proposed_scrap_pct: z.number().optional(),
    delta_pp: z.number().nullable().optional(),
    max_autonomous_delta_pp: z.number().optional(),
    min_confidence: z.number().nullable().optional(),
    unverified_headers_count: Count.optional(),
    missing_required_fields_count: Count.optional(),
    min_autonomous_confidence: z.number().optional(),
    // The policy's own detail (apps/api/src/platform/agents/agent-autonomy-policy.ts).
    field: z.string().optional(),
    items: Count.optional(),
    max_autonomous_items: Count.optional(),
    write_scope: z.string().optional(),
    rule: z.string().nullable().optional(),
    cap: Count.optional(),
    used: Count.optional(),
    remaining: Count.optional(),
    window: z.string().optional(),
    window_started_at: Timestamp.optional(),
    cap_resets_at: Timestamp.optional(),
    configured: z.string().optional(),
    max_autonomy: z.string().optional(),
    ...evidenceCommon,
  })
  .strict()
  .openapi("AgentGenericEvidence");

/** An executed action's result: a reference, never the tool's answer
 * (spec 14 §2.9, D-510 ruling 5). */
export const AgentActionResult = z
  .object({
    ref: z.string().regex(/^[a-z_]+:[0-9a-f-]{36}$/),
    url: z.string().startsWith("/").optional(),
    outcome: z.enum(["pending_approval"]).optional(),
  })
  .strict()
  .openapi("AgentActionResult");

/** The run an action came from, as its card shows it (D-512 ruling 1). */
export const AgentActionRunSummary = z
  .object({
    id: Uuid,
    trigger_type: AgentTriggerType,
    status: AgentRunStatus,
    outcome: AgentRunOutcome.nullable(),
    error: AgentErrorRef.nullable(),
  })
  .strict()
  .openapi("AgentActionRunSummary");

/** The fields every action card carries (spec 14 §2.9, §5's journal rows).
 * `params` are the tool's validated params, which by §3.11 carry no money,
 * address or price (D-534); `impact_minor` is served under the tool's value
 * grants and roles and omitted otherwise (D-535 ruling 1). */
const actionBase = {
  id: Uuid,
  run_id: Uuid.nullable(),
  agent_id: Uuid,
  agent_code: AgentCode,
  tool_version: z.number().int().min(1),
  facility_id: Uuid.nullable(),
  department_id: Uuid.nullable(),
  params: z.record(z.string(), z.unknown()),
  agent_note: z.string().max(500).nullable(),
  agent_note_locale: z.string().max(10).nullable(),
  impact_dimension: AgentToolImpact,
  impact_minor: MinorUnits.optional(),
  impact_currency: Currency.optional(),
  impact_count: Count.nullable(),
  autonomy_applied: AgentAutonomyApplied,
  verdict_reason: z.string().max(64).nullable(),
  status: AgentActionStatus,
  decided_by: Uuid.nullable(),
  decided_at: Timestamp.nullable(),
  decision_note: z.string().max(2_000).nullable(),
  executed_at: Timestamp.nullable(),
  result: AgentActionResult.nullable(),
  error: AgentErrorRef.nullable(),
  reversibility: AgentReversibility,
  undo_action_id: Uuid.nullable(),
  undone_by: Uuid.nullable(),
  undone_at: Timestamp.nullable(),
  expires_at: Timestamp,
  created_at: Timestamp,
  updated_at: Timestamp,
  // For the caller (§7.2, D-511 ruling 2).
  can_approve: z.boolean(),
  can_undo: z.boolean(),
  undo_mode: AgentUndoMode,
  compensation_key: z.string().nullable(),
  run: AgentActionRunSummary.nullable(),
};

const moneyVariant = <T extends AgentMoneyEvidenceTool>(tool: T) =>
  z
    .object({
      ...actionBase,
      tool_code: z.literal(tool),
      evidence: AGENT_MONEY_EVIDENCE[tool].nullable(),
    })
    .strict();

/** The generic variant's `tool_code` is any write that has no money variant. */
const GenericToolCode = z
  .string()
  .regex(/^[a-z]+\.[a-z0-9_]+$/)
  .refine((code) => !(code in AGENT_MONEY_EVIDENCE), {
    message: "a tool with a money evidence variant is served under its own variant",
  });

const genericVariant = z
  .object({
    ...actionBase,
    tool_code: GenericToolCode,
    evidence: AgentGenericEvidence.nullable(),
  })
  .strict();

/** One journal card: a union keyed on `tool_code` (D-506 ruling 8). A tool
 * added to {@link AGENT_MONEY_EVIDENCE} adds its variant here in the same
 * edit; `packages/contracts/test/agents-catalog.test.ts` holds the two lists
 * equal. */
export const AgentAction = z
  .union([
    moneyVariant("inventory.reserve_stock"),
    moneyVariant("inventory.flag_quarantine"),
    moneyVariant("inventory.adjust_stock"),
    moneyVariant("planning.firm_planned_orders"),
    moneyVariant("quality.place_hold"),
    moneyVariant("quality.execute_disposition"),
    moneyVariant("quality.request_rework"),
    moneyVariant("waste.set_disposition"),
    moneyVariant("sales.draft_quote"),
    moneyVariant("sales.send_quote"),
    genericVariant,
  ])
  .openapi("AgentAction");

export const AgentActionListQuery = z
  .object({
    status: AgentActionStatus.optional(),
    agent_id: Uuid.optional(),
    tool_code: z
      .string()
      .regex(/^[a-z]+\.[a-z0-9_]+$/)
      .optional(),
    facility_id: Uuid.optional(),
    department_id: Uuid.optional(),
    needs_my_decision: z.enum(["true", "false"]).optional(),
    impact_min_minor: z.coerce.number().int().min(0).optional(),
    from: Timestamp.optional(),
    to: Timestamp.optional(),
    sort: z.enum(["created_at", "-created_at", "expires_at", "-expires_at"]).optional(),
    cursor: PageCursor,
    limit: PageLimit,
  })
  .strict();

export const AgentActionListResponse = z
  .object({ items: z.array(AgentAction), next_cursor: NextCursor })
  .strict()
  .openapi("AgentActionListResponse");

export const AgentActionDecisionRequest = z
  .object({ note: z.string().trim().min(1).max(2_000).optional() })
  .strict()
  .openapi("AgentActionDecisionRequest");

export const AgentActionUndoResponse = z
  .object({ original: AgentAction, compensating_action: AgentAction.optional() })
  .strict()
  .openapi("AgentActionUndoResponse");

// --- problem codes (spec 14 §5) -----------------------------------------------

/**
 * Every code module 14 raises, and the ten the platform agent layer
 * (`apps/api/src/platform/agents`) already raises. The boot check's refusal
 * reasons (`AGENT_NOTE_PARAM_RESERVED` and the rest) are not problem codes:
 * they stop a process, never answer a request. The module's factories
 * are `apps/api/src/modules/agents/agents-problems.ts`, which builds each
 * code as a literal so the catalog parity test reads them.
 * `IDEMPOTENCY_KEY_REUSE` stays the platform's.
 */
export const agentsProblemCodes = [
  // Raised in apps/api/src/platform/agents (spec 14 §3.1).
  "AGENT_AUTONOMY_ASK_REQUIRED",
  "AGENT_AUTONOMY_CAP_EXCEEDED",
  "AGENT_AUTONOMY_THRESHOLD_EXCEEDED",
  "AGENT_AUTONOMY_THRESHOLD_UNKNOWN",
  "AGENT_AUTONOMY_NOT_ALLOWED",
  "AGENT_TOOL_NOT_AVAILABLE",
  "AGENT_TOOL_PARAMS_INVALID",
  "AGENT_TOOL_TENANT_PARAM_FORBIDDEN",
  "AGENT_TOOL_INVARIANT_BROKEN",
  "AGENT_PRINCIPAL_UNBOUND",
  // The §5 rows.
  "AGENT_IRREVERSIBLE_NOT_AUTONOMOUS",
  "AGENT_NOT_FOUND",
  "AGENT_VERSION_CONFLICT",
  "AGENT_PROFILE_UNKNOWN",
  // The console's switch (spec 15 §5, D-645): a `fault` profile is never set
  // from the console, and a Bedrock profile the probe marked unavailable is
  // refused rather than set to fail every run.
  "AGENT_PROFILE_NOT_OFFERED",
  "AGENT_PROFILE_UNAVAILABLE",
  "AGENT_TOOLS_MISSING",
  "AGENT_BINDING_OUTSIDE_SCOPE",
  "AGENT_TOOL_NOT_FOUND",
  "AGENT_POLICY_LAXER_REQUIRES_CONFIG",
  "AGENT_FACILITY_CEILING_EXCEEDED",
  "AGENT_POLICY_VERSION_CONFLICT",
  "AGENT_POLICY_NOT_ORPHANED",
  "AGENT_PROMPT_VERSION_NOT_FOUND",
  "AGENT_PROMPT_ACTIVATION_CONFLICT",
  "AGENT_TRIGGER_INVALID",
  "AGENT_RUN_SUBJECT_NOT_FOUND",
  "AGENT_PAUSED",
  "AGENT_TOKEN_BUDGET_EXHAUSTED",
  "AGENT_PLATFORM_TOKEN_CEILING_REACHED",
  "AGENT_RUN_NOT_FOUND",
  "AGENT_RUN_NOT_CANCELLABLE",
  "AGENT_IMPACT_FILTER_REQUIRES_VALUE",
  "AGENT_ACTION_NOT_FOUND",
  "AGENT_APPROVAL_REQUIRES_FIELD_VISIBILITY",
  "AGENT_ACTION_EXPIRED",
  "AGENT_ACTION_ALREADY_DECIDED",
  "AGENT_ACTION_NOT_UNDOABLE",
  // §5's approve, when the agent's policy row disables the tool (D-543).
  "AGENT_TOOL_DISABLED",
  // A tenant the ensure has not reached has no settings row (§2.12, D-540).
  "AGENT_SETTINGS_NOT_FOUND",
  // The engine (§3.2, §3.10, §3.11, D-519 ruling 4).
  "AGENT_MODEL_UNAVAILABLE",
  "AGENT_MODEL_ACCESS_DENIED",
  "AGENT_MODEL_REPLAY_DIVERGED",
  "AGENT_RUN_INTERRUPTED",
  // §9 item 16's run limits (D-543 ruling 6).
  "AGENT_RUN_TURN_LIMIT",
  "AGENT_RUN_WALL_CLOCK",
  "AGENT_TOOL_FAILED_TWICE",
  "AGENT_TEXT_CARRIES_PRICE",
  "AGENT_SUBJECT_UNOWNED",
  // A tool the agent's principal does not see (§3.2's visibility check): the
  // call's answer to the model and a refused action's error code.
  "AGENT_TOOL_NOT_VISIBLE",
] as const;

export type AgentsProblemCode = (typeof agentsProblemCodes)[number];

/** The log markers the monitoring stack's metric filters match (D-530 ruling
 * 1), shared so the code and the filter cannot drift. */
export const AGENT_LOG_MARKERS = {
  runFailedFinal: "AGENT_RUN_FAILED_FINAL",
  modelAccessDenied: "AGENT_MODEL_ACCESS_DENIED",
  meteringMismatch: "AGENT_METERING_MISMATCH",
} as const;
